import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useFormSelector } from '@elfsight/widget-form-builder/widget';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { EmptyMessage } from './components/empty-message';
import { Title1 } from './components/typography';
import { CurrentTimeslot } from './datetime/current-timeslot';
import { useDatetime } from './datetime/datetime-provider';
import { SlotPicker } from './datetime/slot-picker';
import { Form } from './form/form';
import { SuccessMessage } from './form/success-message/success-message';
import { ServiceCard } from './service/service-card/service-card';
import { ServiceSelector } from './service/service-selector';
import { useServices } from './service/services-provider';
import { useResetBookingState } from './utils/use-reset-booking-state';

export function Content() {
  const { afterBookingAction, widgetTitle } = useSettings();
  const { selectedTimeslot } = useDatetime();
  const { currentService, services, setCurrentService } = useServices();
  const { status } = useFormSelector();
  const servicesLengthRef = useRef(services.length);
  const resetBookingState = useResetBookingState();

  const isSingleService = services.length === 1;
  const hasNoServices = services.length === 0;

  useEffect(() => {
    const servicesLengthChanged = servicesLengthRef.current !== services.length;

    if (servicesLengthChanged) {
      servicesLengthRef.current = services.length;
    }

    if (hasNoServices) {
      return;
    }

    if (isSingleService) {
      setCurrentService(services[0]);
      return;
    }

    if (!servicesLengthChanged) {
      const targetService = services.find(
        (service) => service.id === currentService?.id
      );
      setCurrentService(targetService);
      return;
    }

    resetBookingState();
  }, [
    services,
    isSingleService,
    hasNoServices,
    currentService,
    setCurrentService,
    resetBookingState
  ]);

  const renderWithHeader = (children: ReactNode, title: string) => {
    return (
      <Container>
        <Title>{title}</Title>

        {children}
      </Container>
    );
  };

  const renderMainContent = useMemo(() => {
    if (hasNoServices) {
      return (
        <>
          <EmptyMessage type="services" />
          <SlotPicker />
        </>
      );
    }

    const showFormOrSlotPicker = selectedTimeslot ? <Form /> : <SlotPicker />;

    if (currentService) {
      return (
        <>
          <div>
            <ServiceCard
              service={currentService}
              canChange={!isSingleService}
            />
            {selectedTimeslot && <CurrentTimeslot canChange />}
          </div>
          {showFormOrSlotPicker}
        </>
      );
    }

    return <ServiceSelector />;
  }, [currentService, selectedTimeslot, isSingleService, hasNoServices]);

  if (status === 'success' && afterBookingAction === 'message') {
    return <SuccessMessage />;
  }

  return renderWithHeader(renderMainContent, widgetTitle);
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'content',
    element: 'container'
  })
})`
  position: relative;
  display: flex;
  gap: 20px;
  min-width: 0;
  width: 100%;
  flex-direction: column;
`;

const Title = styled(Title1).attrs({
  className: createReadableClass({
    component: 'content',
    element: 'title'
  })
})``;
